import { createServerFn } from '@tanstack/react-start';
import { getCookie, setCookie, deleteCookie, getRequestUrl } from '@tanstack/react-start/server';
import { captcha } from '@worldcoo/monolith/captcha';
import { findNgoByEmail, listAllNgoSummaries, updateNgoLastLogin } from '@worldcoo/monolith/apis/portals';
import { getAllDraftNgos } from '@worldcoo/monolith/apis/backoffice/server';
import {
	generateMagicLinkToken,
	sendNgoMagicLinkEmail,
	validateMagicLinkToken,
} from '@worldcoo/monolith/communications/ngo-magic-link';
import * as z from 'zod';
import { signCookie, verifySignedCookie, parseIp } from './server-functions-shared.server';

// Emails allowed to use the impersonation feature (server-side only)
const ADMIN_EMAILS = new Set([
	'romulo.ochoa@worldcoo.com',
	'aitor.guerrero@worldcoo.com',
	'cristina.duran@worldcoo.com',
]);

export const requestNgoLoginFn = createServerFn({ method: 'POST' })
	.inputValidator(
		z.object({
			email: z.email(),
			locale: z.string().optional(),
			captchaToken: z.string(),
			redirect: z.string().optional(),
		}),
	)
	.handler(async ({ data: { email, locale, captchaToken, redirect } }) => {
		// Validate reCAPTCHA before processing login
		const requestIp = parseIp();
		if (!requestIp) throw new Error('Cannot determine client IP');
		const captchaResult = await captcha.validate(captchaToken, requestIp);
		if (!captchaResult.captchaPassed) {
			return { success: false, captchaPassed: false as const };
		}

		const lowerEmail = email.toLowerCase();
		const isAdmin = ADMIN_EMAILS.has(lowerEmail);

		// Helper to send magic link email (logs errors but doesn't throw, to avoid leaking account existence)
		const sendMagicLink = async (targetEmail: string, ngoId: string | undefined, admin: boolean) => {
			try {
				const token = generateMagicLinkToken(targetEmail, ngoId, admin);
				const origin = new URL(getRequestUrl()).origin;
				const callbackParams = new URLSearchParams({ token });
				if (redirect) callbackParams.set('redirect', redirect);
				const callbackUrl = `${origin}/auth/callback?${callbackParams.toString()}`;
				await sendNgoMagicLinkEmail(targetEmail, callbackUrl, locale);
			} catch (err) {
				console.error('Failed to send magic link email:', err instanceof Error ? err.message : String(err));
			}
		};

		// Admin users get a special admin session for impersonation
		if (isAdmin) {
			await sendMagicLink(lowerEmail, undefined, true);
			return { success: true, captchaPassed: true as const, directLogin: false, isAdmin: true } as const;
		}

		// Check both draft NGOs (from onboarding) and real NGOs (already in the system)
		const [drafts, ngo] = await Promise.all([getAllDraftNgos(), findNgoByEmail(lowerEmail)]);
		const draft = drafts.find((d) => d.contact1Email.toLowerCase() === lowerEmail);

		const matchedId = draft?.id ?? ngo?.id;

		if (matchedId) {
			await sendMagicLink(lowerEmail, matchedId, false);
		}

		// Always return success to prevent account existence enumeration
		return { success: true, captchaPassed: true as const, directLogin: false, isAdmin: false } as const;
	});

export const validateMagicLinkFn = createServerFn({ method: 'POST' })
	.inputValidator(z.object({ token: z.string() }))
	.handler(async ({ data: { token } }) => {
		const result = validateMagicLinkToken(token);

		if (!result.valid) {
			return { success: false, error: result.error } as const;
		}

		const { payload } = result;

		if (payload.isAdmin) {
			// Admin magic link: set admin session
			setCookie('ngo-admin-session', signCookie(payload.email), {
				httpOnly: true,
				sameSite: 'strict',
				path: '/',
				maxAge: 86400,
				secure: true,
			});
			return { success: true, isAdmin: true } as const;
		}

		// Regular NGO magic link: set NGO session
		if (!payload.ngoId) {
			return { success: false, error: 'missing_ngo' } as const;
		}
		setCookie('ngo-session', signCookie(payload.ngoId), {
			httpOnly: true,
			sameSite: 'strict',
			path: '/',
			maxAge: 86400,
			secure: true,
		});
		// Track last login (fire-and-forget, don't block auth)
		updateNgoLastLogin(payload.ngoId).catch((err) =>
			console.error('Failed to update lastLogin:', err instanceof Error ? err.message : String(err)),
		);
		return { success: true, isAdmin: false } as const;
	});

export const impersonateNgoFn = createServerFn({ method: 'POST' })
	.inputValidator(z.object({ email: z.email() }))
	.handler(async ({ data: { email } }) => {
		// Verify the caller is an authenticated admin
		const adminEmail = verifySignedCookie(getCookie('ngo-admin-session'));
		if (!adminEmail || !ADMIN_EMAILS.has(adminEmail)) {
			return { success: false, error: 'unauthorized' } as const;
		}

		const lowerEmail = email.toLowerCase();

		const [drafts, ngo] = await Promise.all([getAllDraftNgos(), findNgoByEmail(lowerEmail)]);
		const draft = drafts.find((d) => d.contact1Email.toLowerCase() === lowerEmail);

		const matchedId = draft?.id ?? ngo?.id;
		if (!matchedId) {
			return { success: false, error: 'not_found' } as const;
		}

		setCookie('ngo-session', signCookie(matchedId), {
			httpOnly: true,
			sameSite: 'strict',
			path: '/',
			maxAge: 86400,
			secure: true,
		});
		console.info('NGO_IMPERSONATION', {
			admin: adminEmail,
			targetNgoId: matchedId,
			targetEmail: lowerEmail,
			timestamp: new Date().toISOString(),
		});

		return { success: true, ngoId: matchedId } as const;
	});

export const listAllNgosFn = createServerFn().handler(async () => {
	// Verify the caller is an authenticated admin
	const adminEmail = verifySignedCookie(getCookie('ngo-admin-session'));
	if (!adminEmail || !ADMIN_EMAILS.has(adminEmail)) {
		return { success: false, ngos: [] } as const;
	}

	const [drafts, ngos] = await Promise.all([getAllDraftNgos(), listAllNgoSummaries()]);

	const ngoList = [
		...drafts.map((d) => ({ id: d.id, name: d.name, email: d.contact1Email, type: 'draft' as const })),
		...ngos.map((n) => ({ id: n.id, name: n.name ?? '', email: n.email, type: 'active' as const })),
	];

	return { success: true, ngos: ngoList } as const;
});

export const logoutNgoFn = createServerFn({ method: 'POST' }).handler(() => {
	deleteCookie('ngo-session', { path: '/' });
	deleteCookie('ngo-admin-session', { path: '/' });
	return { success: true } as const;
});
